import { useEffect, useState } from 'react';

import useAuthenticatedAPIServiceCall from '@frolight/hooks/useAuthenticatedAPIServiceCall';
import * as gatewayService from '@frolight/services/api/gatewayService';

const useChartData = (gatewayId, unitDeviceId, temperatureSensorDeviceId, contactorDeviceId, timeRange) => {

  const [ loading, setLoading ] = useState(true);
  const [ data, setData ] = useState(null);
  const [ error, setError ] = useState(null);

  const authenticatedAPIServiceCall = useAuthenticatedAPIServiceCall();

  useEffect(() => {
    let mounted = true;

    const getSettingsMessagesForUnitAbortController = new AbortController();
    const getStatusMessagesForContactorPeripheralAbortController = new AbortController();
    const getTelemetryMessagesForPeripheralAbortController = new AbortController();

    const getUnitSettingsMessages = async () => {
      const messages = await authenticatedAPIServiceCall(
        gatewayService.getSettingsMessagesForUnit,
        { 
          abortController: getSettingsMessagesForUnitAbortController,
          opts: {
            gatewayId: gatewayId,
            unitDeviceId: unitDeviceId,
            filters: {
              dataFrom: timeRange.from,
              dataTo: timeRange.to
            }
          }
        }
      );
      return messages;
    };

    const getContactorPeripheralStatusMessages = async () => {
      const messages = await authenticatedAPIServiceCall(
        gatewayService.getStatusMessagesForPeripheral,
        {
          abortController: getStatusMessagesForContactorPeripheralAbortController,
          opts: {
            gatewayId: gatewayId,
            unitDeviceId: unitDeviceId,
            peripheralDeviceId: contactorDeviceId,
            filters: {
              dataFrom: timeRange.from,
              dataTo: timeRange.to
            }
          }
        }
      );
      return messages;
    }

    const getTemperatureSensorTelemetryMessages = async () => {
      const messages = await authenticatedAPIServiceCall(
        gatewayService.getTelemetryMessagesForPeripheral,
        {
          abortController: getTelemetryMessagesForPeripheralAbortController,
          opts: {
            gatewayId: gatewayId,
            unitDeviceId: unitDeviceId,
            peripheralDeviceId: temperatureSensorDeviceId,
            filters: {
              dataFrom: timeRange.from,
              dataTo: timeRange.to
            }
          }
        }
      );
      return messages;
    };

    const getMessages = async () => {
      if (mounted) setLoading(true);

      try {
        const messagesFetchingFunctions = [getUnitSettingsMessages, getContactorPeripheralStatusMessages, getTemperatureSensorTelemetryMessages];
        const [
          getUnitSettingsMessagesResponse,
          getContactorPeripheralStatusMessagesResponse,
          getTemperatureSensorTelemetryMessagesResponse
        ] = await Promise.all(messagesFetchingFunctions.map(async (fn) => {
          return await fn();
        }));

        let allMessages;

        const unitSettingsMessages = (getUnitSettingsMessagesResponse.data || []).reduce((parsedMessages, msg) => {
          const parsedMessage = {
            timestamp: msg.DataTimestamp * 1000
          };
          // Settings reported
          if (msg.Payload?.threshold_on || msg.Payload?.threshold_off) {
            parsedMessage.threshold = {
              on: msg.Payload.threshold_on ? Number(msg.Payload.threshold_on.temperature) : undefined,
              off: msg.Payload.threshold_off ? Number(msg.Payload.threshold_off.temperature) : undefined
            };
          }
          // Settings update
          if (msg.Payload?.Updates?.threshold_on || msg.Payload?.Updates?.threshold_off) {
            parsedMessage.threshold = {
              on: msg.Payload.Updates?.threshold_on ? Number(msg.Payload.Updates.threshold_on.temperature) : undefined,
              off: msg.Payload.Updates?.threshold_off ? Number(msg.Payload.Updates.threshold_off.temperature) : undefined
            };
          };
          if (parsedMessage.threshold) {
            return [...parsedMessages, parsedMessage];
          }
          return parsedMessages;
        }, []);

        allMessages = unitSettingsMessages;

        (getContactorPeripheralStatusMessagesResponse.data || []).forEach((contactorStatusMsg) => {
          if (contactorStatusMsg.Payload.output || contactorStatusMsg.Payload.errors) {
            let contactorStatus, overruleStatus;

            // Possible errors
            // CONTACTOR_NOT_ACTIVATED = "Contact niet aangetrokken"
            // CONTACTOR_ACTIVATED = "Contactor aangetrokken"
            // NO_POWER_AFTER_CONTACTOR = "Geen spanning op uitgang"
            // POWER_AFTER_CONTACTOR = "Spanning op uitgang"
            // OVER_RULE_ACTIVE = "Over rule actief"

            const outputIsOn = contactorStatusMsg.Payload.output && contactorStatusMsg.Payload?.output === 'on';

            if (!contactorStatusMsg.Payload.errors || contactorStatusMsg.Payload.errors.length === 0) {
              contactorStatus = outputIsOn ? 'On': 'Off';
              overruleStatus = 'Off';
            } else {
              if (outputIsOn) {
                contactorStatus = 'On';

                if (contactorStatusMsg.Payload.errors.includes('Over rule actief')) {
                  overruleStatus = 'On';
                } else {
                  overruleStatus = 'Off';
                }
              } else {
                overruleStatus = contactorStatusMsg.Payload.errors.includes('Over rule actief') ? 'On' : 'Off';

                if (
                  contactorStatusMsg.Payload.errors.includes('Over rule actief') &&
                  contactorStatusMsg.Payload.errors.includes('Spanning op uitgang') &&
                  contactorStatusMsg.Payload.errors.includes('Contactor aangetrokken')
                ) {
                  contactorStatus = 'On';
                } else {
                  contactorStatus = 'Off';
                }
              }
            }

            

            
            const msgWithSameTimestampIdx = allMessages.findIndex(msg => msg.timestamp === contactorStatusMsg.DataTimestamp * 1000);
            if (msgWithSameTimestampIdx === -1) {
              allMessages.push({
                timestamp: contactorStatusMsg.DataTimestamp * 1000,
                contactorStatus: contactorStatus,
                overruleStatus: overruleStatus
              });
            } else {
              allMessages[msgWithSameTimestampIdx].contactorStatus = contactorStatus;
              allMessages[msgWithSameTimestampIdx].overruleStatus = overruleStatus;
            }
          }
        });

        if (!getTemperatureSensorTelemetryMessagesResponse.data.length) {
          // No temperature data for the given period, so don't render chart
          mounted && setError({ type: 'NO_TEMPERATURE_DATA_FOR_PERIOD' });
        } else {
          getTemperatureSensorTelemetryMessagesResponse.data.forEach((telemetryMsg) => {
            const msgWithSameTimestampIdx = allMessages.findIndex(msg => msg.timestamp === telemetryMsg.DataTimestamp * 1000);
            const temperatureData = telemetryMsg.Payload?.data?.temperature;
            const temperatureObj = {
              min: temperatureData.min,
              max: temperatureData.max,
              current: temperatureData.current
            };
            if (msgWithSameTimestampIdx === -1) {
              allMessages.push({
                timestamp: telemetryMsg.DataTimestamp * 1000,
                temperature: temperatureObj
              });
            } else {
              allMessages[msgWithSameTimestampIdx].temperature = temperatureObj;
            }
          });

          const sortedMessages = allMessages.toSorted((a, b) => a.timestamp - b.timestamp);

          let newChartData = {
            timeRange: {
              from: timeRange.from,
              to: timeRange.to
            },
            data: sortedMessages
          };
  
          mounted && setData(newChartData);
        }

      } catch (err) {
        if (err.name !== 'AbortError') {
          mounted && setError(err);
        }
      } finally {
        mounted && setLoading(false);
      }
    };

    if (
      gatewayId &&
      unitDeviceId &&
      temperatureSensorDeviceId &&
      contactorDeviceId &&
      timeRange
    ) {
      setError(null);
      setData(null);
      getMessages();
    }

    return () => {
      mounted = false;
      getSettingsMessagesForUnitAbortController.abort();
      getStatusMessagesForContactorPeripheralAbortController.abort();
      getTelemetryMessagesForPeripheralAbortController.abort();
    }
  }, [ gatewayId, unitDeviceId, temperatureSensorDeviceId, contactorDeviceId, timeRange ]);

  return { loading, error, data };
};

export default useChartData;
